import { apiFetch } from "../config/apiFetch";

function extractFileName(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (plainMatch?.[1]) return plainMatch[1].trim();
  return null;
}

export type AuthenticatedFilePayload = { blob: Blob; fileName: string | null };

export async function fetchAuthenticatedFile(url: string): Promise<AuthenticatedFilePayload> {
  const res = await apiFetch(url, { cache: "no-store" });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) detail = String(body.error);
    } catch {
      /* no-op */
    }
    throw new Error(`Could not fetch document: ${detail}`);
  }
  return {
    blob: await res.blob(),
    fileName: extractFileName(res.headers.get("content-disposition")),
  };
}

export async function openAuthenticatedFile(url: string): Promise<void> {
  const popup = window.open("about:blank", "_blank", "noopener,noreferrer");
  try {
    const { blob } = await fetchAuthenticatedFile(url);
    const objectUrl = URL.createObjectURL(blob);
    if (popup && !popup.closed) popup.location.href = objectUrl;
    else window.open(objectUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    if (popup && !popup.closed) popup.close();
    throw error;
  }
}

export async function downloadAuthenticatedFile(url: string, fallbackFileName = "document"): Promise<void> {
  const { blob, fileName } = await fetchAuthenticatedFile(url);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName || fallbackFileName;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
